import {
  Badge,
  Box,
  Divider,
  Group,
  Stack,
  Text,
  ThemeIcon,
  Tooltip,
} from '@mantine/core';
import {
  IconArrowRight,
  IconCash,
  IconMinus,
  IconPencil,
} from '@tabler/icons-react';
import { useGetTariff } from 'api/resource/tariff';
import { Diff, DiffEdit } from 'deep-diff';
import { Debit, EOB } from 'interfaces';
import React from 'react';
import {
  filterDisplayableDebitDifferences,
  formatField,
} from './TimelineUtils';
import { getEOBActionTypeColor } from 'utils/badgeColors';

interface DebitChangesProps {
  changeDetails: {
    added: Debit[];
    removed: Debit[];
    modified: {
      id: string;
      differences: Diff<any, any>[];
    }[];
    before: Debit[];
    after: Debit[];
    changedBy: string;
    updatedAt: string;
  };
  green: string;
  red: string;
}

const formatAmount = (value: string | number | null | undefined) => {
  if (value === null || value === undefined || value === '') return 'None';
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return String(value);
  return numericValue.toLocaleString(undefined, {
    style: 'currency',
    currency: 'CAD',
  });
};

const getPathKey = (path: unknown) =>
  Array.isArray(path) ? path.join('.') : String(path ?? '');

const getDebitLabel = (debit?: Debit) => {
  const tariff = debit?.tariff;
  if (tariff) return `Tariff ${tariff.prefix}${tariff.code}`;
  if (debit?.remittanceLine?.tariffCode) {
    const prefix = debit.remittanceLine.prefix ?? '';
    return `Tariff ${prefix}${debit.remittanceLine.tariffCode}`;
  }
  return 'Payment';
};

const EOBBadges = ({ eobs }: { eobs?: EOB[] }) => {
  if (!eobs?.length) return null;

  return (
    <Group gap={4} wrap="nowrap">
      {eobs.map((eob) => (
        <Tooltip key={eob.id} label={eob.description} position="top">
          <Badge color={getEOBActionTypeColor(eob.actionType)} radius="xl">
            {eob.code}
          </Badge>
        </Tooltip>
      ))}
    </Group>
  );
};

const DebitRow = ({
  debit,
  icon,
  iconColor,
  before,
  after,
}: {
  debit: Debit;
  icon: React.ReactNode;
  iconColor: string;
  before: string;
  after: string;
}) => {
  const { data: tariff } = useGetTariff({ id: debit.tariffID || '' });
  const label = tariff
    ? `Tariff ${tariff.prefix}${tariff.code}`
    : getDebitLabel(debit);

  return (
    <Group py={6} gap="sm" align="flex-start" data-testid="debit-change-row">
      <ThemeIcon size={24} radius="sm" variant="light" color={iconColor}>
        {icon}
      </ThemeIcon>
      <Text fw={600} w={150} maw="100%" lh={1.4}>
        {label}
      </Text>
      <Group gap="xs" style={{ flex: '1 1 260px' }}>
        <Badge variant="light" color="gray" radius="xl">
          {before}
        </Badge>
        <IconArrowRight size={16} color="gray" />
        <Badge variant="light" color="blue" radius="xl">
          {after}
        </Badge>
        <EOBBadges eobs={debit.eobs} />
      </Group>
    </Group>
  );
};

const DebitDiffRow = ({
  debit,
  diff,
}: {
  debit?: Debit;
  diff: DiffEdit<string, string>;
}) => {
  const pathKey = getPathKey(diff.path);
  const field = formatField(pathKey);
  const isAmountField = [
    'requestedAmount',
    'paidAmount',
    'adjustment',
  ].includes(pathKey);
  const before = isAmountField
    ? formatAmount(diff.lhs)
    : String(diff.lhs ?? 'None');
  const after = isAmountField
    ? formatAmount(diff.rhs)
    : String(diff.rhs ?? 'None');

  return (
    <Group py={6} gap="sm" align="flex-start" data-testid="debit-modified-diff">
      <ThemeIcon size={24} radius="sm" variant="light" color="blue">
        <IconPencil size={14} />
      </ThemeIcon>
      <Text fw={600} w={150} maw="100%" lh={1.4}>
        {getDebitLabel(debit)}
      </Text>
      <Group gap="xs" style={{ flex: '1 1 260px' }}>
        <Text size="sm" c="dimmed" w={105} maw="100%">
          {field}:
        </Text>
        <Badge variant="light" color="gray" radius="xl">
          {before}
        </Badge>
        <IconArrowRight size={16} color="gray" />
        <Badge variant="light" color="blue" radius="xl">
          {after}
        </Badge>
      </Group>
    </Group>
  );
};

export const DebitChanges: React.FC<DebitChangesProps> = ({
  changeDetails: { added, removed, modified, before, after },
}) => {
  const displayableModified = (modified ?? [])
    .map((item) => ({
      ...item,
      differences: filterDisplayableDebitDifferences(item.differences),
    }))
    .filter((item) => item.differences.length > 0);
  const addedDebits =
    added?.length || removed?.length || displayableModified.length
      ? added ?? []
      : after;
  const removedDebits = removed?.length ? removed : [];

  return (
    <Box data-testid="debit-changes-root">
      {addedDebits.length > 0 && (
        <Stack gap={0} data-testid="debit-changes-added">
          {addedDebits.map((debit) => (
            <DebitRow
              key={`debit-added-${debit.id}`}
              debit={debit}
              icon={<IconCash size={14} />}
              iconColor="green"
              before={formatAmount(debit.requestedAmount)}
              after={formatAmount(debit.paidAmount)}
            />
          ))}
        </Stack>
      )}

      {removedDebits.length > 0 && (
        <Stack gap={0} data-testid="debit-changes-removed">
          {removedDebits.map((debit, index) => (
            <Box key={`debit-removed-${debit.id}`}>
              {(addedDebits.length > 0 || index > 0) && <Divider my="xs" />}
              <DebitRow
                debit={debit}
                icon={<IconMinus size={14} />}
                iconColor="red"
                before={formatAmount(debit.paidAmount)}
                after="Removed"
              />
            </Box>
          ))}
        </Stack>
      )}

      {displayableModified.length > 0 && (
        <Stack gap={0} data-testid="debit-changes-modified">
          {displayableModified.map((modifiedDebit, index) => {
            const debit =
              after.find((item) => item.id === modifiedDebit.id) ??
              before.find((item) => item.id === modifiedDebit.id);

            return (
              <Box key={`debit-modified-${modifiedDebit.id}-${index}`}>
                {(addedDebits.length > 0 ||
                  removedDebits.length > 0 ||
                  index > 0) && <Divider my="xs" />}
                {modifiedDebit.differences?.map((diff, diffIndex) => (
                  <DebitDiffRow
                    key={`debit-modified-${modifiedDebit.id}-${diffIndex}`}
                    debit={debit}
                    diff={diff as DiffEdit<string, string>}
                  />
                ))}
              </Box>
            );
          })}
        </Stack>
      )}
    </Box>
  );
};
